import { ArrowDown, ArrowUp, Play, Radio, SkipForward, X } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { ProjectLogo } from '@/pages/Judges/components/ProjectLogo';
import type { StageAdmin } from '@/types';
import { StageTimer } from './StageTimer';
import { useStageAction } from './useStageAction';

export function StageEditor({
  eventSlug,
  stage,
  otherStages,
}: {
  eventSlug: string;
  stage: StageAdmin;
  otherStages: Array<{ id: string; name: string; slug: string }>;
}) {
  const { pending, error, run } = useStageAction();
  const [order, setOrder] = useState<string[]>(
    stage.projects
      .slice()
      .sort((a, b) => a.presentationOrder - b.presentationOrder)
      .map((p) => p.projectId),
  );
  const [currentDraft, setCurrentDraft] = useState<string | null>(
    stage.projects.find((p) => p.isCurrentlyPresenting)?.projectId ?? null,
  );
  const [randomizeOpen, setRandomizeOpen] = useState(false);

  const stageUrl = `/admin/${eventSlug}/stages/${stage.id}`;

  const slotById = new Map(stage.projects.map((p) => [p.projectId, p]));
  // Whoever follows the project on stage (the first one when nobody is on yet), for the
  // one-tap "next" during the demos.
  const currentIndex = currentDraft ? order.indexOf(currentDraft) : -1;
  const nextId = order[currentIndex + 1] ?? null;

  const move = (id: string, delta: number) => {
    const idx = order.indexOf(id);
    if (idx < 0) return;
    const next = [...order];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setOrder(next);
    run('patch', `${stageUrl}/order`, { orderedProjectIds: next });
  };

  const moveToStage = (projectId: string, toStageId: string) => {
    setOrder((prev) => prev.filter((id) => id !== projectId));
    if (currentDraft === projectId) setCurrentDraft(null);
    run('post', `${stageUrl}/move`, { toStageId, projectId });
  };

  const randomize = () => {
    const shuffled = [...order];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    run('patch', `${stageUrl}/order`, { orderedProjectIds: shuffled }, () => {
      setOrder(shuffled);
      setRandomizeOpen(false);
    });
  };

  const removeFromStage = (projectId: string) => {
    if (!confirm('Remove this project from the stage?')) return;
    setOrder((prev) => prev.filter((id) => id !== projectId));
    if (currentDraft === projectId) setCurrentDraft(null);
    run('delete', `${stageUrl}/projects/${projectId}`);
  };

  const togglePresenting = (projectId: string) => {
    const next = currentDraft === projectId ? null : projectId;
    setCurrentDraft(next);
    run('patch', `${stageUrl}/presenting`, { projectId: next });
  };

  const remove = () => {
    if (!confirm('Delete this stage?')) return;
    run('delete', stageUrl);
  };

  return (
    <div className="flex flex-col gap-5">
      <label className="flex items-center justify-between gap-3 border p-3">
        <span className="flex flex-col">
          <span className="font-medium text-sm">Final stage</span>
          <span className="text-muted-foreground text-xs">
            One per event — marking this one unmarks any other.
          </span>
        </span>
        <Switch
          checked={stage.isFinal}
          disabled={pending}
          onCheckedChange={(isFinal) =>
            run('patch', `${stageUrl}/final`, { isFinal })
          }
        />
      </label>
      <StageTimer
        eventSlug={eventSlug}
        stageId={stage.id}
        stageSlug={stage.slug}
        timer={stage.timer}
      />
      <section className="flex flex-col gap-2">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <Label>Project order</Label>
          <p className="text-muted-foreground text-xs">
            Tap “Present” (or the project) to put it on stage.
          </p>
        </div>

        {order.length > 0 && (
          // On a phone it stays at the top while scrolling the order: who is on, and
          // one tap to bring on the next one.
          <div className="sticky top-0 z-10 flex items-center gap-3 border bg-background p-2 shadow-sm">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="font-mono text-[10px] text-muted-foreground uppercase">
                On stage
              </span>
              <span className="truncate font-medium text-sm">
                {currentDraft
                  ? (slotById.get(currentDraft)?.projectName ?? '?')
                  : 'Nobody'}
              </span>
            </div>
            <Button
              className="h-10 shrink-0 rounded-none"
              disabled={pending || nextId === null}
              onClick={() => nextId && togglePresenting(nextId)}
            >
              <SkipForward className="mr-1 h-4 w-4" />
              {nextId
                ? `Next: ${slotById.get(nextId)?.projectName ?? '?'}`
                : 'End of order'}
            </Button>
          </div>
        )}

        {order.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No projects in this stage. Use “Move to…” on another stage to send
            projects here.
          </p>
        ) : (
          <ol className="flex flex-col gap-1">
            {order.map((id, i) => {
              const isPresenting = currentDraft === id;
              const slot = slotById.get(id);
              const status = slot?.submissionStatus;
              return (
                <li
                  key={id}
                  className={cn(
                    'flex flex-col gap-2 border p-2 transition-colors sm:flex-row sm:items-center',
                    isPresenting && 'border-primary bg-primary/5',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => togglePresenting(id)}
                    disabled={pending}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:opacity-50"
                  >
                    <span className="w-6 shrink-0 text-center font-mono text-muted-foreground text-xs">
                      {i + 1}
                    </span>
                    <ProjectLogo
                      project={{
                        name: slot?.projectName ?? '?',
                        logoUrl: slot?.logoUrl ?? null,
                      }}
                      className="h-10 w-10 text-xs"
                    />
                    <span className="flex min-w-0 flex-col gap-1">
                      <span className="break-words font-medium leading-tight hover:underline">
                        {slot?.projectName ?? '?'}
                      </span>
                      <span className="flex flex-wrap items-center gap-1">
                        <Badge
                          variant="outline"
                          className="rounded-none font-normal text-[10px]"
                        >
                          {slot?.trackName ?? 'No track'}
                        </Badge>
                        <span className="font-mono text-muted-foreground text-xs">
                          /{slot?.teamSlug}
                        </span>
                        {status && status !== 'submitted' && (
                          <Badge
                            variant={
                              status === 'requirement_missing'
                                ? 'destructive'
                                : 'secondary'
                            }
                            className="rounded-none font-mono text-[10px] uppercase"
                          >
                            {status.replace('_', ' ')}
                          </Badge>
                        )}
                      </span>
                    </span>
                  </button>

                  <div className="flex items-center gap-1 sm:shrink-0">
                    <Button
                      className="h-9 rounded-none"
                      size="sm"
                      variant={isPresenting ? 'default' : 'outline'}
                      disabled={pending}
                      onClick={() => togglePresenting(id)}
                    >
                      {isPresenting ? (
                        <>
                          <Radio className="mr-1 h-3.5 w-3.5" />
                          Live
                        </>
                      ) : (
                        <>
                          <Play className="mr-1 h-3.5 w-3.5" />
                          Present
                        </>
                      )}
                    </Button>
                    {otherStages.length > 0 && (
                      <Select
                        value=""
                        onValueChange={(toStageId) => moveToStage(id, toStageId)}
                        disabled={pending}
                      >
                        <SelectTrigger className="h-9 w-28 rounded-none text-xs">
                          <SelectValue placeholder="Move to…" />
                        </SelectTrigger>
                        <SelectContent>
                          {otherStages.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <div className="ml-auto flex items-center">
                      <Button
                        className="h-9 w-9 rounded-none"
                        size="icon"
                        variant="ghost"
                        disabled={pending || i === 0}
                        onClick={() => move(id, -1)}
                        aria-label="Move up"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        className="h-9 w-9 rounded-none"
                        size="icon"
                        variant="ghost"
                        disabled={pending || i === order.length - 1}
                        onClick={() => move(id, 1)}
                        aria-label="Move down"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                      <Button
                        className="h-9 w-9 rounded-none"
                        size="icon"
                        variant="ghost"
                        disabled={pending}
                        onClick={() => removeFromStage(id)}
                        aria-label="Remove from stage"
                        title="Remove from stage"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        {order.length > 1 && (
          <div>
            <Button
              className="rounded-none"
              size="sm"
              variant="outline"
              onClick={() => setRandomizeOpen(true)}
              disabled={pending}
            >
              Randomize order
            </Button>
          </div>
        )}
      </section>

      {error && (
        <p className="bg-destructive/10 p-2 text-destructive text-sm">{error}</p>
      )}

      <div className="border-t pt-3">
        <Button
          className="rounded-none"
          size="sm"
          variant="destructive"
          onClick={remove}
          disabled={pending}
        >
          Delete stage
        </Button>
      </div>

      <Dialog open={randomizeOpen} onOpenChange={setRandomizeOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Randomize project order?</DialogTitle>
            <DialogDescription>
              This will shuffle the {order.length} projects in this stage and
              save the new order immediately. There's no undo.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 flex justify-end gap-2">
            <Button
              className="rounded-none"
              variant="outline"
              onClick={() => setRandomizeOpen(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              className="rounded-none"
              onClick={randomize}
              disabled={pending}
            >
              {pending ? 'Shuffling…' : 'Shuffle'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
